<template>
  <section class="past-ahrs-section" id="gallery">

    <!-- ── HEADER ─────────────────────────────────────── -->
    <div class="section-header">
      <div class="header-inner">
        <div class="event-badge" data-aos="fade-down">
          <span class="badge-dot"></span>
          AHRS 2024 &nbsp;·&nbsp; Kigali Convention Centre, Rwanda
        </div>
        <h2 class="section-title" data-aos="fade-up">
          Past Summit <span class="title-accent">Highlights</span>
        </h2>
        <p class="section-quote" data-aos="fade-up" data-aos-delay="100">
          "Aligning Human Resources Strategy and Practices to Africa Union Agenda 2063"
        </p>
        <div class="stats-strip" data-aos="fade-up" data-aos-delay="200">
          <div class="stat-item">
            <span class="stat-number">3</span>
            <span class="stat-label">Summit Days</span>
          </div>
          <div class="stat-divider"></div>
          <div class="stat-item">
            <span class="stat-number">300+</span>
            <span class="stat-label">Attendees</span>
          </div>
          <div class="stat-divider"></div>
          <div class="stat-item">
            <span class="stat-number">21</span>
            <span class="stat-label">Countries</span>
          </div>
          <div class="stat-divider"></div>
          <div class="stat-item">
            <span class="stat-number">11</span>
            <span class="stat-label">Moments</span>
          </div>
        </div>
      </div>
    </div>

    <!-- ── BENTO PHOTO GRID ───────────────────────────── -->
    <div class="gallery-container" data-aos="fade-up" data-aos-delay="250">
      <div class="photo-grid">
        <div
          v-for="(image, index) in images"
          :key="index"
          :class="['photo-item', image.layout]"
          @click="openLightbox(image)"
        >
          <img
            :src="image.src"
            :alt="image.alt"
            class="photo-img"
            loading="lazy"
          />
          <span class="photo-tag">{{ image.category }}</span>
          <div class="photo-overlay">
            <div class="overlay-content">
              <h3 class="overlay-title">{{ image.title }}</h3>
              <p class="overlay-desc">{{ image.shortDescription }}</p>
              <span class="overlay-view">
                <i class="fas fa-expand-alt"></i> View Photo
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ── CTA ───────────────────────────────────────── -->
    <div class="cta-container" data-aos="fade-up">
      <div class="cta-card">
        <div class="cta-left">
          <span class="cta-label">Coming Next · 2nd Edition</span>
          <h3 class="cta-title">Join Us for AHRS 2026</h3>
          <p class="cta-text">
            Be part of the next chapter in Africa's HR transformation journey.<br />
            <strong>Mount Meru Hotel · Arusha, Tanzania · 19–23 August 2026</strong>
          </p>
        </div>
        <router-link to="/registration" class="btn-register">
          Register Now <i class="fas fa-arrow-right ms-2"></i>
        </router-link>
      </div>
    </div>

  </section>
</template>

<script>
import { Modal } from "bootstrap";

export default {
  name: "PastAhrsGallery",
  data() {
    return {
      images: [
        // ── Row 1: mass (wide 2-col) | award (portrait 2-row) | member (portrait 2-row)
        {
          src: require("@/assets/images/past Ahrs/mass.jpg"),
          alt: "Conference Audience at Kigali Convention Centre",
          title: "Conference Audience",
          category: "Opening",
          shortDescription: "Hundreds of HR professionals from across Africa gathered at the Kigali Convention Centre",
          description: "Hundreds of HR professionals from across Africa gathered at the Kigali Convention Centre for the inaugural AHRS 2024, demonstrating the continent-wide commitment to workforce transformation.",
          layout: "wide"
        },
        {
          src: require("@/assets/images/past Ahrs/award.jpg"),
          alt: "Award Ceremony Recognition",
          title: "Award Ceremony",
          category: "Recognition",
          shortDescription: "Recognizing excellence in African HR leadership and innovation",
          description: "Recognizing excellence in African HR practices, the AHRS 2024 awards celebrated innovative leaders who are driving meaningful change in their organizations and communities.",
          layout: "portrait"
        },
        {
          src: require("@/assets/images/past Ahrs/member.jpg"),
          alt: "HR Professional Attendee",
          title: "Engaged Professional",
          category: "Attendees",
          shortDescription: "HR professionals from 21 African countries connecting and learning",
          description: "HR professionals from diverse African countries engaging in meaningful conversations, building partnerships that will drive continental workforce development.",
          layout: "portrait"
        },
        // ── Row 2: guest (wide 2-col) fills under mass; award+member still spanning
        {
          src: require("@/assets/images/past Ahrs/guest.jpg"),
          alt: "Distinguished Keynote Speaker",
          title: "Keynote Speaker",
          category: "Keynote",
          shortDescription: "Thought leaders sharing insights on aligning HR with AU Agenda 2063",
          description: "Renowned thought leaders shared transformative insights on aligning HR strategies with Africa Union Agenda 2063, inspiring attendees to envision a bold future for African workforce development.",
          layout: "wide"
        },
        // ── Row 3: guestmic (portrait 2-row) | rtable (wide 2-col) | memberr (portrait 2-row)
        {
          src: require("@/assets/images/past Ahrs/guest with microphone.jpg"),
          alt: "Expert Panelist",
          title: "Expert Panelist",
          category: "Panel",
          shortDescription: "Industry experts exploring cutting-edge HR solutions for Africa",
          description: "Dynamic panel discussions featured industry experts exploring cutting-edge HR technologies, emerging workforce trends, and innovative solutions for Africa's unique challenges.",
          layout: "portrait"
        },
        {
          src: require("@/assets/images/past Ahrs/round table.jpg"),
          alt: "Round Table Networking Session",
          title: "Round Table Networking",
          category: "Networking",
          shortDescription: "Intimate sessions fostering cross-continental collaborations",
          description: "Intimate networking sessions facilitated meaningful connections between HR professionals, fostering collaborations that extend far beyond the summit's conclusion.",
          layout: "wide"
        },
        {
          src: require("@/assets/images/past Ahrs/memberr.jpg"),
          alt: "Summit Delegate",
          title: "Summit Delegates",
          category: "Attendees",
          shortDescription: "Leaders deeply engaged throughout the 3-day summit",
          description: "HR leaders from across the continent deeply engaged in summit sessions, absorbing insights and building their professional networks.",
          layout: "portrait"
        },
        // ── Row 4: talk (wide 2-col) fills under rtable; guestmic+memberr still spanning
        {
          src: require("@/assets/images/past Ahrs/talk.jpg"),
          alt: "Main Stage Panel Discussion",
          title: "Main Stage Panel",
          category: "Panel",
          shortDescription: "High-level discussions charting Africa's HR transformation path",
          description: "High-level panel discussions on the main stage brought together African HR leaders to chart the path forward for workforce transformation across the continent.",
          layout: "wide"
        },
        // ── Row 5: thinking (wide 2-col) | award photos (wide 2-col)
        {
          src: require("@/assets/images/past Ahrs/thinking.jpg"),
          alt: "Summit in Session",
          title: "Summit in Session",
          category: "Sessions",
          shortDescription: "Delegates engaged in thought-provoking deep-dive sessions",
          description: "Deep-dive sessions challenged conventional thinking, encouraging participants to reimagine HR practices and develop innovative approaches to talent management in the African context.",
          layout: "wide"
        },
        {
          src: require("@/assets/images/past Ahrs/award photos.jpg"),
          alt: "Awards of Excellence",
          title: "Awards of Excellence",
          category: "Recognition",
          shortDescription: "Celebrating outstanding contributions to African HR excellence",
          description: "Celebrating outstanding achievements in African HR excellence, recognizing individuals and organizations that exemplify innovation, leadership, and positive impact in their communities.",
          layout: "wide"
        },
        // ── Row 6: Photo — full-width finale (group photo)
        {
          src: require("@/assets/images/past Ahrs/Photo.jpg"),
          alt: "Official AHRS 2024 Group Photo",
          title: "The AHRS 2024 Family",
          category: "Historic",
          shortDescription: "The inaugural AHRS 2024 — a united continent, one HR vision",
          description: "A historic moment capturing the inaugural AHRS 2024 participants, representing a united commitment to transforming Africa's human resources landscape through collective action and shared vision.",
          layout: "full"
        }
      ]
    };
  },
  methods: {
    openLightbox(image) {
      const existing = document.getElementById("galleryModal");
      if (existing) existing.remove();

      document.body.insertAdjacentHTML("beforeend", `
        <div class="modal fade" id="galleryModal" tabindex="-1" aria-hidden="true">
          <div class="modal-dialog modal-xl modal-dialog-centered">
            <div class="modal-content ahrs-modal">
              <div class="modal-header ahrs-modal-header">
                <div class="ahrs-modal-title-block">
                  <span class="ahrs-modal-tag">${image.category}</span>
                  <h5 class="ahrs-modal-title">${image.title}</h5>
                </div>
                <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
              </div>
              <div class="modal-body p-0">
                <div class="ahrs-modal-img-wrap">
                  <img src="${image.src}" alt="${image.alt}" class="ahrs-modal-img">
                  <div class="ahrs-modal-overlay">
                    <p class="ahrs-modal-description">${image.description}</p>
                  </div>
                </div>
              </div>
              <div class="modal-footer ahrs-modal-footer">
                <span class="ahrs-modal-meta">
                  AHRS 2024 &nbsp;·&nbsp; Kigali Convention Centre, Rwanda &nbsp;·&nbsp; 20–22 November 2024
                </span>
              </div>
            </div>
          </div>
        </div>
      `);

      const modal = new Modal(document.getElementById("galleryModal"));
      modal.show();
      document.getElementById("galleryModal").addEventListener("hidden.bs.modal", () => {
        document.getElementById("galleryModal").remove();
      });
    }
  }
};
</script>

<style scoped>
/* ── SECTION ──────────────────────────────────────────── */
.past-ahrs-section {
  background: #eef2ee;
}

/* ── HEADER ───────────────────────────────────────────── */
.section-header {
  background: linear-gradient(135deg, #0c1e11 0%, #1a472a 55%, #2d5f3f 100%);
  padding: 76px 24px 60px;
  position: relative;
  overflow: hidden;
}

.section-header::before {
  content: '';
  position: absolute;
  inset: 0;
  background:
    radial-gradient(ellipse at 12% 55%, rgba(74, 222, 128, 0.10) 0%, transparent 52%),
    radial-gradient(ellipse at 88% 18%, rgba(74, 222, 128, 0.07) 0%, transparent 48%),
    url("data:image/svg+xml,%3Csvg width='64' height='64' viewBox='0 0 64 64' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%234ade80' fill-opacity='0.035'%3E%3Cpath d='M8 0h1v8H8zM0 8v1h8V8zM56 0h1v8h-1zM56 8v1h8V8zM8 56h1v8H8zM0 56v1h8v-1zM56 56h1v8h-1zM56 64v-1h8v1z'/%3E%3C/g%3E%3C/svg%3E");
  pointer-events: none;
}

.header-inner {
  max-width: 780px;
  margin: 0 auto;
  text-align: center;
  position: relative;
  z-index: 1;
}

.event-badge {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  background: rgba(255, 255, 255, 0.09);
  border: 1px solid rgba(255, 255, 255, 0.17);
  color: rgba(255, 255, 255, 0.86);
  padding: 7px 22px;
  border-radius: 100px;
  font-size: 0.82rem;
  font-weight: 500;
  letter-spacing: 0.3px;
  margin-bottom: 28px;
  backdrop-filter: blur(8px);
}

.badge-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #4ade80;
  flex-shrink: 0;
  animation: pulse-dot 2.2s ease-in-out infinite;
}

@keyframes pulse-dot {
  0%, 100% { opacity: 1; transform: scale(1); }
  50%       { opacity: 0.35; transform: scale(1.5); }
}

.section-title {
  font-size: clamp(2rem, 5vw, 3.25rem);
  font-weight: 800;
  color: #ffffff;
  margin: 0 0 16px;
  line-height: 1.1;
  letter-spacing: -0.025em;
}

.title-accent {
  color: #4ade80;
}

.section-quote {
  color: rgba(255, 255, 255, 0.58);
  font-style: italic;
  font-size: 0.96rem;
  line-height: 1.65;
  max-width: 540px;
  margin: 0 auto 38px;
}

/* Stats row */
.stats-strip {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 28px;
}

.stat-number {
  font-size: 1.9rem;
  font-weight: 800;
  color: #4ade80;
  line-height: 1;
}

.stat-label {
  font-size: 0.68rem;
  color: rgba(255, 255, 255, 0.48);
  text-transform: uppercase;
  letter-spacing: 1.3px;
  margin-top: 5px;
}

.stat-divider {
  width: 1px;
  height: 38px;
  background: rgba(255, 255, 255, 0.17);
  flex-shrink: 0;
}

/* ── GALLERY CONTAINER ────────────────────────────────── */
.gallery-container {
  padding: 40px 20px 28px;
  max-width: 1400px;
  margin: 0 auto;
}

/* ── BENTO GRID ───────────────────────────────────────── */
.photo-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-auto-rows: 268px;
  gap: 10px;
  grid-auto-flow: row dense;
}

/* Layout variants — auto-placement fills the bento perfectly */
.photo-item.wide    { grid-column: span 2; }
.photo-item.portrait { grid-row: span 2; }
.photo-item.full    { grid-column: 1 / -1; grid-row: span 2; }

/* ── PHOTO ITEM ───────────────────────────────────────── */
.photo-item {
  position: relative;
  overflow: hidden;
  border-radius: 14px;
  cursor: pointer;
  background: #162b1c;
  display: block;
}

.photo-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.55s cubic-bezier(0.25, 0.46, 0.45, 0.94),
              filter 0.55s ease;
}

.photo-item:hover .photo-img {
  transform: scale(1.07);
  filter: brightness(0.78);
}

/* Always-visible category pill */
.photo-tag {
  position: absolute;
  top: 12px;
  left: 12px;
  background: rgba(8, 24, 12, 0.68);
  color: #a8efbe;
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.3px;
  padding: 4px 11px;
  border-radius: 100px;
  backdrop-filter: blur(6px);
  border: 1px solid rgba(74, 222, 128, 0.22);
  z-index: 2;
  transition: opacity 0.28s ease;
}

.photo-item:hover .photo-tag {
  opacity: 0;
}

/* Hover overlay — slides up */
.photo-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top,
    rgba(4, 18, 8, 0.94) 0%,
    rgba(4, 18, 8, 0.52) 42%,
    rgba(4, 18, 8, 0.08) 100%
  );
  display: flex;
  align-items: flex-end;
  padding: 20px;
  opacity: 0;
  transition: opacity 0.30s ease;
  z-index: 3;
}

.photo-item:hover .photo-overlay {
  opacity: 1;
}

.overlay-content {
  transform: translateY(10px);
  transition: transform 0.30s ease;
}

.photo-item:hover .overlay-content {
  transform: translateY(0);
}

.overlay-title {
  color: #ffffff;
  font-size: 1rem;
  font-weight: 700;
  margin: 0 0 5px;
  line-height: 1.25;
}

.photo-item.full .overlay-title {
  font-size: 1.4rem;
}

.overlay-desc {
  color: rgba(255, 255, 255, 0.70);
  font-size: 0.78rem;
  line-height: 1.45;
  margin: 0 0 10px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.overlay-view {
  color: #4ade80;
  font-size: 0.77rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

/* ── CTA ──────────────────────────────────────────────── */
.cta-container {
  padding: 4px 20px 64px;
  max-width: 1400px;
  margin: 0 auto;
}

.cta-card {
  background: linear-gradient(135deg, #0c1e11 0%, #1a472a 100%);
  border-radius: 18px;
  padding: 42px 52px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 28px;
  flex-wrap: wrap;
  position: relative;
  overflow: hidden;
}

.cta-card::after {
  content: '';
  position: absolute;
  top: -50%;
  right: -8%;
  width: 380px;
  height: 380px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(74, 222, 128, 0.09) 0%, transparent 68%);
  pointer-events: none;
}

.cta-label {
  display: block;
  color: #4ade80;
  font-size: 0.70rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 2.2px;
  margin-bottom: 8px;
}

.cta-title {
  color: #ffffff;
  font-size: 1.8rem;
  font-weight: 800;
  margin: 0 0 8px;
  line-height: 1.2;
}

.cta-text {
  color: rgba(255, 255, 255, 0.62);
  margin: 0;
  font-size: 0.92rem;
  line-height: 1.55;
}

.btn-register {
  display: inline-flex;
  align-items: center;
  background: #4ade80;
  color: #091a0c;
  font-weight: 700;
  font-size: 0.97rem;
  padding: 14px 36px;
  border-radius: 50px;
  text-decoration: none;
  white-space: nowrap;
  flex-shrink: 0;
  transition: background 0.24s, transform 0.24s, box-shadow 0.24s;
  box-shadow: 0 4px 22px rgba(74, 222, 128, 0.28);
  position: relative;
  z-index: 1;
}

.btn-register:hover {
  background: #22c55e;
  color: #091a0c;
  transform: translateY(-2px);
  box-shadow: 0 8px 30px rgba(74, 222, 128, 0.44);
}

/* ── RESPONSIVE ───────────────────────────────────────── */
@media (max-width: 991px) {
  .photo-grid {
    grid-template-columns: repeat(2, 1fr);
    grid-auto-rows: 210px;
    gap: 8px;
  }

  .photo-item.full { grid-row: span 1; }

  .stat-item { padding: 0 20px; }
  .stat-number { font-size: 1.55rem; }
}

@media (max-width: 768px) {
  .section-header { padding: 52px 20px 40px; }

  .photo-grid {
    grid-auto-rows: 185px;
    gap: 7px;
  }

  /* Touch screens: always show overlay since hover doesn't exist */
  .photo-overlay {
    opacity: 1;
    background: linear-gradient(to top, rgba(4,18,8,0.78) 0%, transparent 58%);
  }
  .overlay-content { transform: none; }
  .photo-tag { opacity: 0; }

  .cta-card {
    padding: 32px 28px;
    flex-direction: column;
    text-align: center;
  }
  .cta-title { font-size: 1.45rem; }
  .cta-left { width: 100%; }
}

@media (max-width: 576px) {
  .stats-strip { gap: 20px 0; flex-direction: column; }
  .stat-divider { display: none; }
  .stat-item { padding: 0; }

  .photo-grid {
    grid-auto-rows: 155px;
    gap: 6px;
  }

  .photo-item.full { grid-row: span 2; }

  .overlay-title { font-size: 0.85rem; }
  .overlay-desc { display: none; }
  .overlay-view { font-size: 0.72rem; }

  .gallery-container { padding: 24px 12px 16px; }
  .cta-container { padding: 4px 12px 44px; }
  .cta-card { padding: 26px 20px; }
  .cta-title { font-size: 1.25rem; }
  .btn-register { padding: 13px 28px; font-size: 0.9rem; }
}
</style>

<!-- Modal styles must be global (not scoped) since the modal is injected into document.body -->
<style>
.ahrs-modal {
  background: #0d1f11;
  border: 1px solid rgba(74, 222, 128, 0.18);
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 30px 60px rgba(0, 0, 0, 0.45);
}

.ahrs-modal-header {
  background: linear-gradient(135deg, #0c1e11, #1a472a);
  padding: 18px 24px;
  border-bottom: none !important;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.ahrs-modal-title-block {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.ahrs-modal-tag {
  display: inline-block;
  background: rgba(74, 222, 128, 0.18);
  color: #4ade80;
  border: 1px solid rgba(74, 222, 128, 0.28);
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.6px;
  padding: 3px 11px;
  border-radius: 100px;
}

.ahrs-modal-title {
  color: #ffffff !important;
  font-weight: 700;
  font-size: 1.2rem;
  margin: 0;
}

.ahrs-modal-img-wrap {
  position: relative;
  overflow: hidden;
  background: #081208;
  max-height: 68vh;
}

.ahrs-modal-img {
  width: 100%;
  height: auto;
  max-height: 68vh;
  object-fit: contain;
  display: block;
}

.ahrs-modal-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 22px 26px;
  background: linear-gradient(to top, rgba(4, 15, 7, 0.88) 0%, transparent 100%);
}

.ahrs-modal-description {
  color: rgba(255, 255, 255, 0.88);
  font-size: 0.93rem;
  line-height: 1.62;
  margin: 0;
}

.ahrs-modal-footer {
  background: #081208;
  border-top: 1px solid rgba(74, 222, 128, 0.12) !important;
  padding: 12px 24px !important;
  justify-content: center !important;
}

.ahrs-modal-meta {
  color: rgba(255, 255, 255, 0.38);
  font-size: 0.78rem;
}
</style>
